import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Worklog {
  id: string;
  /** monday.com item ID */
  mondayItemId: string;
  /** monday.com board ID */
  mondayBoardId: string;
  /** monday.com user ID */
  mondayUserId: string;
  /** Date of the worklog entry */
  date: string; // ISO date
  /** Hours logged */
  hours: number;
  /** Description of work performed */
  description: string | null;
  /** monday.com user ID of the person who created this worklog (null for legacy records) */
  createdByUserId: string | null;
  /** Worklog category ID (null for legacy entries) */
  worklogCategoryId: string | null;
  /** Category display name */
  worklogCategoryName: string | null;
  /** Category hex color */
  worklogCategoryColor: string | null;
  /** Whether the category is billable */
  worklogCategoryIsBillable: boolean | null;
  createdAt: string;
  updatedAt: string;
}

export interface GetWorklogsParams {
  /** monday.com user ID (required by backend) */
  userId: string;
  /** Filter by date range (required by backend) */
  startDate: string;
  endDate: string;
}

export interface CreateWorklogPayload {
  /** monday.com item ID */
  mondayItemId: string;
  /** monday.com board ID */
  mondayBoardId: string;
  /** monday.com user ID (the person logging the work) */
  mondayUserId: string;
  date: string;
  hours: number;
  description?: string | null;
  worklogCategoryId?: string;
}

export interface UpdateWorklogPayload {
  date?: string;
  hours?: number;
  description?: string;
  worklogCategoryId?: string;
}

export interface WorklogSummary {
  /** Total hours logged */
  totalHours: number;
}

export interface GetBulkWorklogsParams {
  userIds: string[];
  startDate: string;
  endDate: string;
}

export interface GetWorklogSummaryParams {
  /** monday.com user ID (required by backend) */
  userId: string;
  /** Date range (required by backend) */
  startDate: string;
  endDate: string;
}

// ─── Normalizer ──────────────────────────────────────────────────────────────

/**
 * Normalise a raw worklog object from the backend (which may use snake_case
 * column names from the DB entity) into the camelCase `Worklog` shape the
 * frontend expects.
 */
/** Normalize a date value to YYYY-MM-DD, handling timezone-shifted ISO strings. */
function normalizeDateString(value: unknown): string {
  if (!value) return '';
  const str = String(value);
  // If it's already YYYY-MM-DD, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  // If it's an ISO timestamp, extract the date part correctly
  // by parsing and using UTC components (the DB stores date-only values)
  if (str.includes('T') || str.includes('Z')) {
    const d = new Date(str);
    // Use UTC to avoid timezone shift — the DB 'date' column has no time component
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  }
  return str.slice(0, 10);
}

function normalizeWorklog(raw: Record<string, unknown>): Worklog {
  // Extract category from the joined relation (worklogCategory) or flat fields
  const cat = raw.worklogCategory as Record<string, unknown> | null | undefined;
  return {
    id: String(raw.id ?? ''),
    mondayItemId: String(raw.mondayItemId ?? raw.monday_item_id ?? ''),
    mondayBoardId: String(raw.mondayBoardId ?? raw.monday_board_id ?? ''),
    mondayUserId: String(raw.mondayUserId ?? raw.monday_user_id ?? ''),
    date: normalizeDateString(raw.date),
    hours: Number(raw.hours ?? 0),
    description: (raw.description as string | null) ?? null,
    createdByUserId: (raw.createdByUserId ?? raw.created_by_user_id ?? null) as string | null,
    worklogCategoryId: (raw.worklogCategoryId ?? raw.worklog_category_id ?? cat?.id ?? null) as string | null,
    worklogCategoryName: (raw.worklogCategoryName ?? cat?.name ?? null) as string | null,
    worklogCategoryColor: (raw.worklogCategoryColor ?? cat?.color ?? null) as string | null,
    worklogCategoryIsBillable: cat?.is_billable != null ? Boolean(cat.is_billable) : (raw.worklogCategoryIsBillable as boolean | null ?? null),
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? ''),
  };
}

// ─── API calls ────────────────────────────────────────────────────────────────

/** Fetch worklogs with required filters. */
export async function getWorklogs(
  params: GetWorklogsParams,
): Promise<Worklog[]> {
  const { data } = await apiClient.get<Record<string, unknown>[]>('/worklogs', {
    params: {
      userId: params.userId,
      startDate: params.startDate,
      endDate: params.endDate,
    },
  });
  return data.map(normalizeWorklog);
}

/** Create a new worklog entry. */
export async function createWorklog(
  payload: CreateWorklogPayload,
): Promise<Worklog> {
  const { data } = await apiClient.post<Record<string, unknown>>('/worklogs', payload);
  return normalizeWorklog(data);
}

/** Update an existing worklog entry. */
export async function updateWorklog(
  id: string,
  payload: UpdateWorklogPayload,
): Promise<Worklog> {
  const { data } = await apiClient.patch<Record<string, unknown>>(
    `/worklogs/${id}`,
    payload,
  );
  return normalizeWorklog(data);
}

/** Delete a worklog entry. */
export async function deleteWorklog(id: string): Promise<void> {
  await apiClient.delete(`/worklogs/${id}`);
}

/** Fetch all worklogs for a specific monday.com item. */
export async function getWorklogsByItem(
  itemId: string,
): Promise<Worklog[]> {
  const { data } = await apiClient.get<Record<string, unknown>[]>(`/worklogs/item/${itemId}`);
  return data.map(normalizeWorklog);
}

/** Fetch total logged hours for a specific item. */
export async function getWorklogSummaryForItem(
  itemId: string,
): Promise<WorklogSummary> {
  const { data } = await apiClient.get<WorklogSummary>(`/worklogs/item/${itemId}/summary`);
  return data;
}

/** Fetch individual worklogs for multiple users in a date range. */
export async function getBulkWorklogs(
  params: GetBulkWorklogsParams,
): Promise<Worklog[]> {
  const { data } = await apiClient.get<Record<string, unknown>[]>('/worklogs/bulk', {
    params: {
      userIds: params.userIds.join(','),
      startDate: params.startDate,
      endDate: params.endDate,
    },
  });
  return data.map(normalizeWorklog);
}

export interface ItemUserWorklogSummary {
  mondayItemId: string;
  mondayUserId: string;
  totalHours: number;
}

/** Fetch per-item per-user total worklog hours. */
export async function getWorklogSummaryByItemAndUser(
  boardIds: string[],
  userIds?: string[],
): Promise<ItemUserWorklogSummary[]> {
  const params: Record<string, string> = {
    boardIds: boardIds.join(','),
  };
  if (userIds && userIds.length > 0) {
    params.userIds = userIds.join(',');
  }
  const { data } = await apiClient.get<ItemUserWorklogSummary[]>('/worklogs/summary-by-item-user', { params });
  return data;
}

/** Get aggregated worklog summary (total hours for a user in a date range). */
export async function getWorklogSummary(
  params: GetWorklogSummaryParams,
): Promise<WorklogSummary> {
  const { data } = await apiClient.get<WorklogSummary>('/worklogs/summary', {
    params: {
      userId: params.userId,
      startDate: params.startDate,
      endDate: params.endDate,
    },
  });
  return data;
}
